/**
 * Schema Adapter — converts server template (after groupBy3) to Hogery schema format.
 */

const TYPE_MAP = {
  dxTextBox: "text",
  dxDateBox: "date",
  dxRadioGroup: "radio",
  dxCheckBox: "checkbox",
  SelecBox: "select",
  SelectBoxMulti: "multiselect",
  CustomSelectBox: "select",
  CustomRadioBoxGroup: "radio",
  dxSlider: "number",
  dxTextArea: "textarea",
  phone: "phone",
  img: "file",
  multipleFiles: "file",
  roleSelectBox: "select",
  TextStat: "note",
  Header: "_header",
  Header2: "_header",
  Label: "note",
  dxNumberBox: "number",
  DROPDOWN_DB: "dropdown_db",
  DataSet: "_hidden",
};

const ICON_MAP = {
  group: "user",
  user: "user",
  search: "search",
  policy: "policy",
  account: "account",
  doc: "doc",
  upload: "upload",
  file: "file",
  check: "check",
  save: "save",
  send: "send",
  info: "info",
  alert: "alert",
};

/**
 * Parse options from a Value string.
 * For CustomSelectBox / CustomRadioBoxGroup: "id:name|id:name" -> [{value, label}]
 * For regular radio/select: "opt1|opt2|opt3" -> [{value, label}]
 */
function parseOptions(value, fieldType) {
  if (!value) return [];
  const parts = value.split("|").filter(Boolean);

  if (fieldType === "CustomSelectBox" || fieldType === "CustomRadioBoxGroup") {
    return parts.map((part) => {
      const colonIdx = part.indexOf(":");
      if (colonIdx !== -1) {
        return { value: part.substring(0, colonIdx), label: part.substring(colonIdx + 1) };
      }
      return { value: part, label: part };
    });
  }

  return parts.map((p) => ({ value: p, label: p }));
}

/**
 * Map colSpan from DevExtreme to col in Hogery CSS grid.
 * DevExtreme Form has 2 columns, so:
 *   colSpan=1 → half width → Hogery col=2 (span 3 of 6)
 *   colSpan=2 → full width → Hogery col=1 (span 6 of 6)
 *   colSpan=4 → full width → Hogery col=1
 *   colSpan="" or missing → half width → Hogery col=2
 */
function mapColSpan(colSpan) {
  const cs = parseInt(colSpan);
  if (cs >= 2) return 1;   // full width
  return 2;                  // half width (default)
}

/**
 * Convert a single field definition from server format to Hogery field format.
 */
function convertField(fieldDef) {
  const serverType = fieldDef.Type;
  const hogeryType = TYPE_MAP[serverType] || "text";

  // Headers are not real fields — they start sections
  if (hogeryType === "_header") return null;

  // DataSet fields are hidden (used for data loading only)
  if (hogeryType === "_hidden") return null;

  const field = {
    uid: fieldDef._uid,
    type: hogeryType,
    caption: fieldDef.caption || "",
    required: false,
    requiredExpr: null,
    col: mapColSpan(fieldDef.colSpan),
    visible: fieldDef.visible || "",
    onChangeValue: fieldDef.onChangeValue || "",
    validation: fieldDef.validation || "",
    options: [],
    placeholder: fieldDef.placeholder || "",
    object: fieldDef.object || "",
    defaultValue: fieldDef.defaultValue || "",
    originalType: serverType,
    helpText: fieldDef.helpText || "",
    validationPatameters: fieldDef.validationPatameters || "",
    validationMessage: fieldDef.validationMessage || "",
    radioLayout: fieldDef.radioLayout || "auto",
    cardGroup: fieldDef.cardGroup || "",
    mask: typeof fieldDef.mask === 'string' && fieldDef.mask ? fieldDef.mask : "",
    minValue: fieldDef.minValue ?? "",
    maxValue: fieldDef.maxValue ?? "",
    min: fieldDef.minValue ?? null,
    max: fieldDef.maxValue ?? null,
  };

  // Field-level direction override (optional column in Excel)
  const dir = (fieldDef.direction || "").replace(/"/g, "").trim().toLowerCase();
  if (dir === "rtl" || dir === "ltr") {
    field.direction = dir;
  }

  // Required handling — guard against the string "false" which is truthy in JS
  if (fieldDef.required && fieldDef.required !== "false") {
    if (typeof fieldDef.required === "string" && fieldDef.required !== "true") {
      field.required = true;
      field.requiredExpr = fieldDef.required;
    } else {
      field.required = true;
    }
  }

  // Options for radio/select/multiselect types
  if (
    serverType === "dxRadioGroup" ||
    serverType === "SelecBox" ||
    serverType === "SelectBoxMulti" ||
    serverType === "CustomSelectBox" ||
    serverType === "CustomRadioBoxGroup"
  ) {
    field.options = parseOptions(fieldDef.Value, serverType);
  }

  // Slider: extract min/max from Value
  if (serverType === "dxSlider" && fieldDef.Value) {
    const parts = fieldDef.Value.split("|");
    if (parts.length >= 2) {
      field.min = parseFloat(parts[0]) || 0;
      field.max = parseFloat(parts[1]) || 100;
    }
  }

  // File fields: mark multiple
  if (serverType === "multipleFiles") {
    field.multiple = true;
  }
  if (serverType === "img") {
    field.multiple = false;
  }

  // roleSelectBox: mark as special
  if (serverType === "roleSelectBox") {
    field.isRoleSelect = true;
  }

  // DROPDOWN_DB: parse config from Value JSON
  if (serverType === "DROPDOWN_DB") {
    try {
      const config = typeof fieldDef.Value === "string"
        ? JSON.parse(fieldDef.Value)
        : fieldDef.Value || {};
      field.dataSetName = config.DataSetName || "";
      field.valueField = config.ValueField || "KEY";
      field.displayField = config.DisplayField || "Caption";
      field.filter = config.Filter || "";
      field.hideIfParentEmpty = config.HideIfParentEmpty !== false;
      field.resetFields = config.ResetFields || [];
      field.distinct = config.Distinct === true;
    } catch (e) {
      console.error("DROPDOWN_DB: Failed to parse Value JSON:", e);
    }
  }

  // Note/TextStat/Label: caption becomes the display text
  if (serverType === "TextStat" || serverType === "Label") {
    field.noteText = fieldDef.caption || fieldDef.Value || "";
    field.htmlContent = (fieldDef.caption || "").replace(/\n/g, "<br>");
  }

  return field;
}

/**
 * A display row marked "oldversion" in its validation column belongs to the old
 * renderer only (it duplicates a heading that cards now produce here).
 */
const isOldVersionOnly = (fieldDef) =>
  (fieldDef.validation || "").toLowerCase().includes("oldversion");

/**
 * Split fields into sections based on Header/Header2 boundaries.
 */
function buildSections(fields) {
  const sections = [];
  let currentSection = { title: null, fields: [], isCard: false };

  for (const fieldDef of fields) {
    if (fieldDef.Type === "Header" || fieldDef.Type === "Header2") {
      // If current section has fields, push it
      if (currentSection.fields.length > 0 || currentSection.title !== null) {
        sections.push(currentSection);
      }
      const isCard = (fieldDef.validationPatameters || "").toUpperCase() === "CARD";
      const title = isOldVersionOnly(fieldDef) ? null : fieldDef.caption || null;
      currentSection = { title, fields: [], isCard };
      continue;
    }

    if (isOldVersionOnly(fieldDef)) continue;

    const converted = convertField(fieldDef);
    if (converted) {
      currentSection.fields.push(converted);
    }
  }

  // Push the last section
  if (currentSection.fields.length > 0 || currentSection.title !== null) {
    sections.push(currentSection);
  }

  // If no sections were created, create a default one
  if (sections.length === 0) {
    sections.push({ title: null, fields: [] });
  }

  return sections;
}

/**
 * Convert a server template to Hogery schema format.
 * @param {object} template - Server template after groupBy3
 * @param {string} [formName] - Form name for display
 * @returns {object} Hogery schema
 */
export function convertTemplate(template, formName) {
  if (!template || !template.tabs) {
    return { title: formName || "", tabs: [], _hooks: {} };
  }

  const tabs = template.tabs.map((tab) => {
    const par = tab.Value ? tab.Value.split("|") : [];
    const tabId = par[0] || tab._uid;
    const iconKey = par[1] || "";
    const addButtonTitle = par[2] || "";
    const isArray = tab.Type === "array";

    const fields = template[tabId] || [];
    const sections = buildSections(fields);

    const hogeryTab = {
      id: tabId,
      title: tab.caption || "",
      icon: ICON_MAP[iconKey] || "doc",
      isArray,
      arrayLabel: addButtonTitle || tab.caption || "",
      maxItems: tab.maxValue || 10,
      visible: tab.visible || "",
      sections,
    };

    return hogeryTab;
  });

  const schema = {
    title: formName || "",
    tabs,
    _hooks: {
      Run: template.Run || [],
      Func: template.Func || [],
      RunBeforeSend: template.RunBeforeSend || [],
      Validation: template.Validation || [],
      XLClient: template.XLClient || null,
      XLAccount: template.XLAccount || null,
    },
  };

  return schema;
}
